import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { fetchFusionScans } from './services/api';
import FusionTable from './components/FusionTable';
import RiskGraph from './components/RiskGraph';
import TradingViewWidget from './components/TradingViewWidget';
import OverviewPanel from './components/OverviewPanel';
import CombinationCard from './components/CombinationCard';
import AlertsManager from './components/AlertsManager';
import { usePinnedRows } from './hooks/usePinnedRows';
import { playAlertSound, showToastNotification } from './utils/alertUtils';
import './App.css';
import osLogo from './os-logo.png';

// --- Feature flags / defaults ---
const RANKING_FEATURE_ENABLED = false; // Set to false to disable sliders and ranking sort
const DEFAULT_WEIGHTS = { rr: 80, vod: 50, hedge: 30 };

const MIN_PANEL_WIDTH_PERCENT = 15; // Minimum 15% width for panels
const SNAP_TO_CENTER_THRESHOLD_PERCENT = 3; // Snap if within +/- 3% of center

// Function to generate OptionStrat link (UPDATED SYNTAX FOR QUANTITY/DIRECTION & STRIKE FORMATTING)
function generateOptionStratLink(item) {
  // Az item.id logolása továbbra is hasznos, de lehet, hogy az App.js-ben a map-elésnél van a gond az id-vel
  // console.log("Generating link for (item.id might be undefined here, checking full item):", item.id, "Full item data:", item);

  const baseUrl = 'https://optionstrat.com/build/custom/SPX/';
  let legsString = [];

  const formatSymbolForLink = (symbol) => {
    if (typeof symbol !== 'string') {
      console.warn("formatSymbolForLink received non-string symbol:", symbol);
      return '';
    }

    // 1. Remove all spaces
    const spacelessSymbol = symbol.replace(/\s+/g, '');

    // 2. Regex to capture parts: Underlying, Date (YYMMDD), Type (C/P), Strike part before potential trailing zeros
    // Example: SPXW250530C06050000 -> SPXW250530C6050
    // Group 1 (Underlying & Date): SPXW250530
    // Group 2 (Type): C
    // Group 3 (Strike part): 06050 (needs to be converted to 6050)
    // Group 4 (Optional trailing 000 for some tickers, ignored here): 000
    const regex = /^([A-Z0-9]+[0-9]{6})([CP])([0-9]{5})([0-9]{3})?$/;
    const match = spacelessSymbol.match(regex);

    if (match) {
      const underlyingAndDate = match[1]; // e.g., SPXW250530
      const type = match[2];             // e.g., C
      const strikeDigits = match[3];     // e.g., "06050"

      // Convert strikeDigits to number to remove leading zeros, then back to string
      const strike = parseInt(strikeDigits, 10).toString(); 
      
      return `${underlyingAndDate}${type}${strike}`; // e.g., SPXW250530C6050
    } else {
      console.warn("formatSymbolForLink couldn't parse symbol with regex:", spacelessSymbol, "--- using it as is after removing spaces.");
      return spacelessSymbol; // Fallback if regex doesn't match (e.g. for non-standard symbols)
    }
  };

  try {
    // Vertical Spread (Financing: selling a debit spread = creating a credit spread)
    // Original debit: Buy legs[0], Sell legs[1]
    // Resulting credit: Sell legs[0] (qty 1), Buy legs[1] (qty 1)
    if (item.vertical_spread) {
      const vertical = item.vertical_spread; 
      // console.log("Processing Vertical Spread. Object:", vertical);
      if (vertical && vertical.legs && vertical.legs.length === 2) {
        const soldLegSymbol = formatSymbolForLink(vertical.legs[0]?.symbol);
        const boughtLegSymbol = formatSymbolForLink(vertical.legs[1]?.symbol);
        // console.log("Vertical Symbols (formatted):", soldLegSymbol, boughtLegSymbol);
        if (soldLegSymbol) {
          legsString.push(`-.${soldLegSymbol}`); 
        }
        if (boughtLegSymbol) {
          legsString.push(`.${boughtLegSymbol}`);  
        }
        // if (soldLegSymbol && boughtLegSymbol) console.log("Added Vertical legs to string.");
      } else {
        // console.warn("Vertical spread object did not meet criteria (missing legs or not 2 legs).", vertical);
      }
    } else {
      // console.log("No 'vertical_spread' (object) found for this item.");
    }

    // Calendar Spreads (Buying N quantity)
    // Sell short-dated leg (legs[0]), Buy long-dated leg (legs[1])
    if (item.calendar_spread && typeof item.calendar_quantity === 'number' && item.calendar_quantity > 0) {
      const calendar = item.calendar_spread; 
      // console.log("Processing Calendar Spread. Object:", calendar, "Quantity:", item.calendar_quantity);
      if (calendar && calendar.legs && calendar.legs.length === 2) {
        const quantity = item.calendar_quantity;
        const shortLegSymbol = formatSymbolForLink(calendar.legs[0]?.symbol);
        const longLegSymbol = formatSymbolForLink(calendar.legs[1]?.symbol);
        // console.log("Calendar Symbols (formatted):", shortLegSymbol, longLegSymbol);
        if (shortLegSymbol) {
          legsString.push(`.${shortLegSymbol}x-${quantity}`); 
        }
        if (longLegSymbol) {
          legsString.push(`.${longLegSymbol}x${quantity}`);   
        }
        // if (shortLegSymbol && longLegSymbol) console.log("Added Calendar legs to string.");
      } else {
        // console.warn("Calendar spread object did not meet criteria (missing legs or not 2 legs).", calendar);
      }
    } else {
      // console.warn("Calendar quantity not valid or no calendar_spread object.", item.calendar_quantity, item.calendar_spread);
    }

    // Diagonal Spread (Buying diagonal_quantity, default 1)
    // Sell short-dated leg (legs[0]), Buy long-dated leg (legs[1])
    if (item.diagonal_spread) {
      const diagonal = item.diagonal_spread; 
      if (diagonal && diagonal.legs && diagonal.legs.length === 2) {
        const diagQty = item.diagonal_quantity || 1;
        const shortLegSymbol = formatSymbolForLink(diagonal.legs[0]?.symbol);
        const longLegSymbol = formatSymbolForLink(diagonal.legs[1]?.symbol);
        if (shortLegSymbol) {
          legsString.push(diagQty > 1 ? `.${shortLegSymbol}x-${diagQty}` : `-.${shortLegSymbol}`); 
        }
        if (longLegSymbol) {
          legsString.push(diagQty > 1 ? `.${longLegSymbol}x${diagQty}` : `.${longLegSymbol}`);  
        }
      }
    }

    // console.log("Final legsString before join:", legsString);
    if (legsString.length === 0) {
      // console.warn("No legs generated for item ID:", item.id, "Item data:", item);
      return null;
    }
    return baseUrl + legsString.join(',');

  } catch (e) {
    const itemId = item && typeof item.id !== 'undefined' ? item.id : 'UNKNOWN_ID';
    console.error(`Error in generateOptionStratLink for item ID: ${itemId}`, "Item data snippet:", { id: itemId, cs: !!item.calendar_spread, ds: !!item.diagonal_spread, vs: !!item.vertical_spread }, "Error:", e);
    return null;
  }
}

// Copied from FusionTable.js for sorting in App.js
const customNumericSort = (rowA, rowB, columnId) => {
  // Simplified for direct value comparison, assuming rowA and rowB are the values themselves
  const numA = (rowA === null || rowA === undefined) ? -Infinity : Number(rowA);
  const numB = (rowB === null || rowB === undefined) ? -Infinity : Number(rowB);

  if (numA < numB) {
    return -1;
  }
  if (numA > numB) {
    return 1;
  }
  return 0;
};

function App() {
  const [fusionData, setFusionData] = useState([]); // displayed list (sorted)
  const [fusionBaseData, setFusionBaseData] = useState([]); // raw from backend
  const [selectedCombination, setSelectedCombination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null); // New state for the timestamp
  // ranking weights (0..100)
  const getInitialWeight = (key, defVal) => {
    try { const v = localStorage.getItem(key); if (v !== null) return Number(v); } catch (_) {}
    return defVal;
  };
  const [wRR, setWRR] = useState(() => getInitialWeight('rank_w_rr', DEFAULT_WEIGHTS.rr));
  const [wHedge, setWHedge] = useState(() => getInitialWeight('rank_w_hedge', DEFAULT_WEIGHTS.hedge));
  const [wVoD, setWVoD] = useState(() => getInitialWeight('rank_w_vod', DEFAULT_WEIGHTS.vod));
  const getInitialToggle = (key, defaultVal = true) => {
    try {
      const v = localStorage.getItem(key);
      if (v === 'true') return true;
      if (v === 'false') return false;
      return defaultVal;
    } catch (_) {
      return defaultVal;
    }
  };
  const [showOS, setShowOS] = useState(() => getInitialToggle('showOSCol', true));
  const [showTOS, setShowTOS] = useState(() => getInitialToggle('showTOSCol', true));
  const [showAnalysis, setShowAnalysis] = useState(() => getInitialToggle('showAnalysis', false));
  const [showOnlyPinned, setShowOnlyPinned] = useState(() => getInitialToggle('showOnlyPinned', false));
  const getInitialFusionType = () => {
    try {
      const v = sessionStorage.getItem('fusionType');
      if (v === 'front' || v === 'back') return v;
    } catch (_) {}
    return 'back';
  };
  const [fusionType, setFusionType] = useState(getInitialFusionType);

  // theme state
  const getPreferredTheme = () => {
    try {
      const fromStorage = localStorage.getItem('theme');
      if (fromStorage === 'light' || fromStorage === 'dark') return fromStorage;
      const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      return prefersDark ? 'dark' : 'light';
    } catch (e) {
      return 'light';
    }
  };
  const [theme, setTheme] = useState(getPreferredTheme);

  const [tableWidthPercent, setTableWidthPercent] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    try {
      const v = sessionStorage.getItem('viewMode');
      if (v === 'split' || v === 'grid') return v;
    } catch (_) {}
    return 'split';
  });
  const [gridColumns, setGridColumns] = useState(() => {
    try {
      const v = sessionStorage.getItem('gridColumns');
      if (v) return parseInt(v);
    } catch (_) {}
    return 2;
  });
  const [isSwitchingView, setIsSwitchingView] = useState(false);
  const [tableMetricMode, setTableMetricMode] = useState(() => {
    try {
      const v = sessionStorage.getItem('tableMetricMode');
      if (v === 'delta' || v === 'price') return v;
    } catch (_) {}
    return 'delta';
  });
  const [sorting, setSorting] = useState(() => {
    try {
      const v = sessionStorage.getItem('fusion_sorting');
      return v ? JSON.parse(v) : [];
    } catch { return []; }
  });
  const [columnFilters, setColumnFilters] = useState(() => {
    try {
      const v = sessionStorage.getItem('fusion_columnFilters');
      return v ? JSON.parse(v) : [];
    } catch { return []; }
  });

  useEffect(() => {
    try { sessionStorage.setItem('fusion_sorting', JSON.stringify(sorting)); } catch {}
  }, [sorting]);

  useEffect(() => {
    try { sessionStorage.setItem('fusion_columnFilters', JSON.stringify(columnFilters)); } catch {}
  }, [columnFilters]);
  const [isAutoRefresh, setIsAutoRefresh] = useState(() => {
    try {
      return localStorage.getItem('isAutoRefresh') === 'true';
    } catch (_) {
      return false;
    }
  });
  
  const [gridPage, setGridPage] = useState(1);
  const itemsPerPage = 30;
  const gridScrollRef = useRef(null);
  
  const handlePageChange = (newPage) => {
    setGridPage(newPage);
    if (gridScrollRef.current) {
      gridScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  const [allAlertsConfigs, setAllAlertsConfigs] = useState(() => {
    const loadConfig = (type) => {
      try {
        const specificKey = `alertsConfig_${type}`;
        let stored = localStorage.getItem(specificKey);
        
        // Migration: If no specific key, check the legacy shared key (only for 'back' by default or if first time)
        if (!stored && type === 'back') {
          stored = localStorage.getItem('alertsConfig');
        }

        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.rules) {
            parsed.rules = parsed.rules.map(rule => {
              if (!rule.conditions) {
                return { id: rule.id, conditions: [{ metric: rule.metric, operator: rule.operator, value: rule.value }] };
              }
              return rule;
            });
          }
          return { soundId: 'chime', ...parsed };
        }
      } catch (e) {}
      return { enabled: false, soundEnabled: true, toastEnabled: true, soundId: 'chime', rules: [] };
    };

    return {
      front: loadConfig('front'),
      back: loadConfig('back')
    };
  });

  const alertsConfig = allAlertsConfigs[fusionType];
  const setAlertsConfig = useCallback((newVal) => {
    setAllAlertsConfigs(prev => ({
      ...prev,
      [fusionType]: typeof newVal === 'function' ? newVal(prev[fusionType]) : newVal
    }));
  }, [fusionType]);

  const [isAlertsModalOpen, setIsAlertsModalOpen] = useState(false);
  const [alertHighlightedRowIds, setAlertHighlightedRowIds] = useState(new Set());
  const alertedIdsRef = useRef(new Set());
  const alertsConfigRef = useRef(alertsConfig);

  useEffect(() => {
    alertsConfigRef.current = alertsConfig;
    // When the user changes rules or toggles alerts, clear the "already alerted" set
    // so the new/modified rules can re-evaluate all rows on next refresh.
    alertedIdsRef.current = new Set();
  }, [alertsConfig]);

  // Live tick to refresh the "updated X ago" label
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  // Save current type's alertsConfig whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(`alertsConfig_${fusionType}`, JSON.stringify(alertsConfig));
    } catch (e) {}
  }, [alertsConfig, fusionType]);

  // Handle switching types: clear visual state
  useEffect(() => {
    setAlertHighlightedRowIds(new Set());
    setGridPage(1); // Reset pagination on type change
    // Note: we don't necessarily clear alertedIdsRef here because 
    // it prevents re-triggering for the same IDs in this session.
  }, [fusionType]);

  const mainContentRef = useRef(null);
  const dividerWidth = 10; // Must match CSS .divider width

  // Data driving the Analysis panel (keeps in sync with table filters, not with ranking sort order)
  const [analysisData, setAnalysisData] = useState([]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('theme', theme); } catch (e) {}
  }, [theme]);

  useEffect(() => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    // Allow direct access on localhost for development
    if (isLocalhost) {
      return;
    }

    // On production, check if we are inside an iframe.
    if (window.self === window.top) {
      // If not in an iframe, block access.
      document.title = "Hozzáférés megtagadva";
      const rootEl = document.getElementById('root');
      if (rootEl) {
        rootEl.innerHTML = `
          <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; background-color: #fff; display: flex; justify-content: center; align-items: center; text-align: center;">
            <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;">
              <h1>Hozzáférés megtagadva</h1>
              <p>Ez az alkalmazás kizárólag a <a href="https://opcioguru.com" target="_blank" rel="noopener noreferrer">opcioguru.com</a> oldalon beágyazva érhető el.</p>
            </div>
          </div>
        `;
      }
    }
  }, []);

  const fetchCounterRef = useRef(0);

  const loadData = useCallback(async (isRefresh = false) => {
    fetchCounterRef.current += 1;
    const currentFetchId = fetchCounterRef.current;

    if (!isRefresh) {
      setLoading(true);
    }
    setIsRefreshing(true);
    setError(null);
    try {
      const responseData = await fetchFusionScans(fusionType);
      
      if (currentFetchId !== fetchCounterRef.current) {
        return; // A newer fetch has started, ignore this one to prevent StrictMode race conditions
      }
      
      // Ensure responseData and responseData.combinations are valid
      if (responseData && responseData.combinations) {
        const combinationsArray = responseData.combinations;
        const dataWithIdsAndLinks = combinationsArray.map((item, index) => ({
          ...item,
          id: `combo-${index}`, // Ensure 'id' is unique and suitable for keys
          optionStratLink: generateOptionStratLink(item)
        }));
        setFusionBaseData(dataWithIdsAndLinks);
        setFusionData(
          RANKING_FEATURE_ENABLED
            ? applyRankingSort(dataWithIdsAndLinks, wRR, wHedge, wVoD)
            : dataWithIdsAndLinks
        );
        setLastUpdatedAt(responseData.last_updated_at); // Set the timestamp

        // --- Alerting Logic ---
        let newHighlightedRowIds = new Set();
        const currentAlertsConfig = alertsConfigRef.current;
        if (currentAlertsConfig.enabled && currentAlertsConfig.rules && currentAlertsConfig.rules.length > 0) {
            let triggeredAlertsCount = 0;
            const newAlertedSet = new Set(alertedIdsRef.current);
            let playSound = false;
            let showToast = false;
            let matchedRuleSignatures = new Set();
            const METRIC_LABELS = { rr_ratio: 'R/R', net_delta: 'Net Delta', min_pl_death_valley: 'VoD', total_debit: 'Cost' };

            for (const item of dataWithIdsAndLinks) {
                if (!newAlertedSet.has(item.id)) {
                    let matchedAnyRule = false;
                    for (const rule of currentAlertsConfig.rules) {
                        const conditions = rule.conditions || [rule]; // Fallback just in case
                        let allConditionsMet = true;

                        for (const cond of conditions) {
                            let itemValue = null;
                            if (cond.metric === 'rr_ratio') itemValue = item.rr_ratio;
                            else if (cond.metric === 'net_delta') itemValue = item.net_delta;
                            else if (cond.metric === 'min_pl_death_valley') itemValue = item.min_pl_death_valley;
                            else if (cond.metric === 'total_debit') itemValue = item.costs?.net_strategy_cost_after_vertical_credit ?? item.total_debit;

                            if (typeof itemValue !== 'number') {
                                allConditionsMet = false;
                                break;
                            }

                            if ((cond.operator === '>' && itemValue <= cond.value) || 
                                (cond.operator === '<' && itemValue >= cond.value)) {
                                allConditionsMet = false;
                                break;
                            }
                        }

                        if (allConditionsMet && conditions.length > 0) {
                            matchedAnyRule = true;
                            const sig = conditions.map(c => `${METRIC_LABELS[c.metric] || c.metric}${c.operator}${c.value}`).join(' AND ');
                            matchedRuleSignatures.add(sig);
                            break;
                        }
                    }

                    if (matchedAnyRule) {
                        triggeredAlertsCount++;
                        newAlertedSet.add(item.id);
                        newHighlightedRowIds.add(item.id);
                        if (currentAlertsConfig.soundEnabled) playSound = true;
                        if (currentAlertsConfig.toastEnabled) showToast = true;
                    }
                }
            }

            if (triggeredAlertsCount > 0) {
                alertedIdsRef.current = newAlertedSet;
                if (playSound) {
                    playAlertSound(currentAlertsConfig.soundId);
                }
                if (showToast) {
                    const rulesStr = Array.from(matchedRuleSignatures).join(' | ');
                    showToastNotification(`Fusion Scanner Alert (${fusionType.toUpperCase()})`, {
                        body: `Rules: ${rulesStr}\n(${triggeredAlertsCount} new combination${triggeredAlertsCount > 1 ? 's' : ''})`,
                        icon: '/favicon.ico'
                    });
                }
            }
        }
        setAlertHighlightedRowIds(newHighlightedRowIds);
        // --- End Alerting Logic ---

        if (dataWithIdsAndLinks.length === 0) {
          setSelectedCombination(null);
        }
      } else {
        // Handle cases where responseData or responseData.combinations might be missing
        // This could be due to an error structure not caught by the catch block
        // or if the PHP script returns an unexpected format on certain errors.
        console.warn("Received unexpected data structure from fetchFusionScans:", responseData);
        setFusionData([]); // Ensure fusionData is an array
        setSelectedCombination(null);
        // Optionally set an error message if this case is not expected
        // setError("Failed to parse scan data."); 
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
      setFusionData([]); // Ensure fusionData is an array in case of error
      setSelectedCombination(null);
      console.error(err);
    }
    setLoading(false);
    setIsRefreshing(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fusionType]);

  useEffect(() => {
    loadData(false);
  }, [loadData]);

  // Auto-refresh logic
  useEffect(() => {
    let intervalId = null;
    if (isAutoRefresh) {
      intervalId = setInterval(() => {
        loadData(true);
      }, 5 * 60 * 1000); // 5 minutes
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isAutoRefresh, loadData]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleViewModeChange = (newMode) => {
    if (newMode === viewMode) return;
    setIsSwitchingView(true);
    setTimeout(() => {
      setViewMode(newMode);
      try { sessionStorage.setItem('viewMode', newMode); } catch (_) {}
      setIsSwitchingView(false);
    }, 400); // 400ms loader transition
  };

  const handleGridColumnsChange = (cols) => {
    setGridColumns(cols);
    try { sessionStorage.setItem('gridColumns', cols); } catch (_) {}
  };

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !mainContentRef.current) return;

    const containerRect = mainContentRef.current.getBoundingClientRect();
    const newTableWidthPx = e.clientX - containerRect.left;
    const totalContainerWidthPx = containerRect.width - dividerWidth;

    let newTableWidthPercent = (newTableWidthPx / totalContainerWidthPx) * 100;

    const lowerSnapBound = 50 - SNAP_TO_CENTER_THRESHOLD_PERCENT;
    const upperSnapBound = 50 + SNAP_TO_CENTER_THRESHOLD_PERCENT;

    if (newTableWidthPercent >= lowerSnapBound && newTableWidthPercent <= upperSnapBound) {
      newTableWidthPercent = 50;
    }

    if (newTableWidthPercent < MIN_PANEL_WIDTH_PERCENT) {
      newTableWidthPercent = MIN_PANEL_WIDTH_PERCENT;
    }
    if (newTableWidthPercent > (100 - MIN_PANEL_WIDTH_PERCENT)) {
      newTableWidthPercent = 100 - MIN_PANEL_WIDTH_PERCENT;
    }

    setTableWidthPercent(newTableWidthPercent);
  }, [isDragging, dividerWidth]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleSelectCombination = (combination) => {
    setSelectedCombination(combination);
  };

  // --- Pinned rows (persisted per fusion type) ---
  const { isPinned, togglePin, clearAll: clearAllPins, partition: partitionPins, pinCount } = usePinnedRows(fusionType);

  const { pinnedRows, nonPinnedRows: nonPinnedRowsAll } = useMemo(
    () => partitionPins(fusionData),
    [partitionPins, fusionData]
  );
  const nonPinnedRows = showOnlyPinned ? [] : nonPinnedRowsAll;
  
  const totalGridPages = Math.ceil(nonPinnedRows.length / itemsPerPage);
  const paginatedRows = useMemo(() => {
    const start = (gridPage - 1) * itemsPerPage;
    return nonPinnedRows.slice(start, start + itemsPerPage);
  }, [nonPinnedRows, gridPage, itemsPerPage]);

  // Sync analysisData with fusionData whenever the underlying dataset changes
  // (e.g. user toggles Front/Back fusion). FusionTable will afterwards overwrite
  // it with the visible (filtered/sorted) rows via onAnalysisDataUpdate.
  useEffect(() => {
    setAnalysisData(fusionData);
  }, [fusionData]);

  const graphWidthPercent = 100 - tableWidthPercent;

  // Derive current SPX price from selected row (fallback: first row)
  const spxPrice = useMemo(() => {
    const source = selectedCombination || (fusionData && fusionData.length > 0 ? fusionData[0] : null);
    if (!source) return null;
    const price = source?.calendar_spread?.legs?.[0]?.stockprice 
      ?? source?.diagonal_spread?.legs?.[0]?.stockprice 
      ?? source?.vertical_spread?.legs?.[0]?.stockprice;
    const num = typeof price === 'number' ? price : Number(price);
    return Number.isFinite(num) ? num : null;
  }, [selectedCombination, fusionData]);

  const formatIndex = (value, digits = 2) => {
    if (value === null || value === undefined || !Number.isFinite(value)) return '—';
    try {
      return value.toLocaleString('en-US', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
    } catch {
      return value.toFixed(digits);
    }
  };

  const formatUsd = (value) => {
    if (value === null || value === undefined || !Number.isFinite(value)) return '—';
    try {
      return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    } catch {
      return `$${Math.round(value).toLocaleString('en-US')}`;
    }
  };

  // --- Ranking helpers ---
  const computeDisplayMetrics = useCallback((row) => {
    const rr = Number(row?.rr_ratio);
    const vodRaw = Number(row?.min_pl_death_valley);
    const vodDisp = Number.isFinite(vodRaw) ? vodRaw * 100 : null;
    const hedgeRaw = Number(row?.costs?.net_strategy_cost_after_vertical_credit);
    const hedgeDisp = Number.isFinite(hedgeRaw) ? (hedgeRaw < 0 ? Math.abs(hedgeRaw) : -hedgeRaw) * 100 : null;
    return { rr, vodDisp, hedgeDisp };
  }, []);

  const applyRankingSort = useCallback((rows, rrW, hedgeW, vodW) => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const metrics = rows.map(r => ({ r, ...computeDisplayMetrics(r) }));
    const bounds = (key) => {
      const vals = metrics.map(m => m[key]).filter(v => v !== null && v !== undefined && Number.isFinite(v));
      let min = Math.min(...vals), max = Math.max(...vals);
      if (!Number.isFinite(min) || !Number.isFinite(max)) { min = 0; max = 1; }
      if (min === max) max = min + 1;
      return { min, max };
    };
    const bRR = bounds('rr');
    const bH = bounds('hedgeDisp');
    const bV = bounds('vodDisp');
    const wr = (rrW || 0) / 100, wh = (hedgeW || 0) / 100, wv = (vodW || 0) / 100;
    const scoreOf = (m) => (
      wr * ((m.rr - bRR.min) / (bRR.max - bRR.min)) +
      wh * ((m.hedgeDisp - bH.min) / (bH.max - bH.min)) +
      wv * ((m.vodDisp - bV.min) / (bV.max - bV.min))
    );
    const withScore = metrics.map(m => ({ ...m.r, ranking_score: scoreOf(m) }));
    withScore.sort((a,b) => (b.ranking_score ?? -Infinity) - (a.ranking_score ?? -Infinity));
    return withScore;
  }, [computeDisplayMetrics]);

  useEffect(() => {
    if (RANKING_FEATURE_ENABLED) {
      setFusionData(applyRankingSort(fusionBaseData, wRR, wHedge, wVoD));
      try { localStorage.setItem('rank_w_rr', String(wRR)); } catch {}
      try { localStorage.setItem('rank_w_hedge', String(wHedge)); } catch {}
      try { localStorage.setItem('rank_w_vod', String(wVoD)); } catch {}
    } else {
      setFusionData(fusionBaseData);
    }
  }, [wRR, wHedge, wVoD, fusionBaseData, applyRankingSort]);

  const setVoDAndHedge = () => {
    setWVoD(50);
    setWHedge(30);
    try { localStorage.setItem('rank_w_vod','50'); localStorage.setItem('rank_w_hedge','30'); } catch {}
  };

  return (
    <div className="App">
      {showAnalysis && (
        <div style={{
          marginBottom: 12,
          border: '1px solid var(--border)',
          borderRadius: '12px',
          background: 'var(--surface)'
        }}>
          <OverviewPanel data={analysisData} onSelectCombination={setSelectedCombination} />
        </div>
      )}
      {lastUpdatedAt && fusionData.length > 0 && (
        <div className="last-updated-timestamp">
          <div className="button-row toolbar" role="toolbar" aria-label="Main actions">

            {/* Group 1: Data */}
            <div className="toolbar-group" aria-label="Data">
              <button
                onClick={() => loadData(true)}
                disabled={isRefreshing}
                className="refresh-button"
                title={isRefreshing ? 'Refreshing…' : 'Refresh data now'}
                aria-label="Refresh data"
              >
                {isRefreshing ? '…' : '↻'}
              </button>
              <button
                onClick={() => {
                  setIsAutoRefresh(prev => {
                    const next = !prev;
                    try { localStorage.setItem('isAutoRefresh', String(next)); } catch (_) {}
                    return next;
                  });
                }}
                title={`Auto-refresh every 5 minutes — currently ${isAutoRefresh ? 'ON' : 'OFF'}`}
                className={`auto-refresh-button ${isAutoRefresh ? 'active' : ''}`}
                aria-pressed={isAutoRefresh}
              >
                AUTO 5m
              </button>
            </div>

            <div className="toolbar-divider" aria-hidden="true" />

            {/* Group 2: Dataset */}
            <div className="toolbar-group" aria-label="Dataset">
              <div className="tb-segmented" role="group" aria-label="Fusion type">
                <button
                  onClick={() => {
                    setFusionType('back');
                    try { sessionStorage.setItem('fusionType', 'back'); } catch (_) {}
                  }}
                  title="Show Back Fusion combinations"
                  className={`tb-seg-btn tb-seg-back ${fusionType === 'back' ? 'is-active' : ''}`}
                  aria-pressed={fusionType === 'back'}
                >
                  BACK
                </button>
                <button
                  onClick={() => {
                    setFusionType('front');
                    try { sessionStorage.setItem('fusionType', 'front'); } catch (_) {}
                  }}
                  title="Show Front Fusion combinations (variable diagonal)"
                  className={`tb-seg-btn tb-seg-front ${fusionType === 'front' ? 'is-active' : ''}`}
                  aria-pressed={fusionType === 'front'}
                >
                  FRONT
                </button>
              </div>
            </div>

            <div className="toolbar-divider" aria-hidden="true" />

            {/* Group 3: View */}
            <div className="toolbar-group" aria-label="View">
              <div className="tb-segmented tb-segmented-inset" role="group" aria-label="Layout mode">
                <button
                  onClick={() => handleViewModeChange('split')}
                  title="Split view — table + charts"
                  className={`tb-seg-btn tb-seg-icon ${viewMode === 'split' ? 'is-active' : ''}`}
                  aria-pressed={viewMode === 'split'}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="9" y1="3" x2="9" y2="21"></line>
                  </svg>
                </button>
                <button
                  onClick={() => handleViewModeChange('grid')}
                  title="Grid view — compare combinations side by side"
                  className={`tb-seg-btn tb-seg-icon ${viewMode === 'grid' ? 'is-active' : ''}`}
                  aria-pressed={viewMode === 'grid'}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7"></rect>
                    <rect x="14" y="3" width="7" height="7"></rect>
                    <rect x="14" y="14" width="7" height="7"></rect>
                    <rect x="3" y="14" width="7" height="7"></rect>
                  </svg>
                </button>
              </div>

              {viewMode === 'grid' && (
                <div className="tb-segmented tb-segmented-inset" role="group" aria-label="Grid columns">
                  {[1, 2, 3].map(cols => (
                    <button
                      key={cols}
                      onClick={() => handleGridColumnsChange(cols)}
                      title={`${cols} column${cols > 1 ? 's' : ''} per row`}
                      className={`tb-seg-btn tb-seg-num ${gridColumns === cols ? 'is-active' : ''}`}
                      aria-pressed={gridColumns === cols}
                    >
                      {cols}
                    </button>
                  ))}
                </div>
              )}

              <button
                onClick={() => {
                  setShowAnalysis(prev => {
                    const next = !prev;
                    try { localStorage.setItem('showAnalysis', String(next)); } catch (_) {}
                    return next;
                  });
                }}
                title={`Overview / analysis panel — ${showAnalysis ? 'ON' : 'OFF'}`}
                className={`tb-btn ${showAnalysis ? 'tb-btn-success-active' : ''}`}
                aria-pressed={showAnalysis}
                aria-label="Toggle overview panel"
              >
                📊
              </button>

              <button
                onClick={() => {
                  setShowOS(prev => {
                    const next = !prev;
                    try { localStorage.setItem('showOSCol', String(next)); } catch (_) {}
                    return next;
                  });
                }}
                title={`OptionStrat column — ${showOS ? 'shown' : 'hidden'}`}
                className={`tb-btn ${showOS ? 'tb-btn-success-active' : ''}`}
                aria-pressed={showOS}
                aria-label="Toggle OptionStrat column"
              >
                <img src={osLogo} alt="" className="tb-btn-img" style={{ opacity: showOS ? 1 : 0.6, filter: showOS ? 'none' : 'grayscale(40%)' }} />
              </button>

              <button
                onClick={() => {
                  setShowTOS(prev => {
                    const next = !prev;
                    try { localStorage.setItem('showTOSCol', String(next)); } catch (_) {}
                    return next;
                  });
                }}
                title={`ThinkOrSwim column — ${showTOS ? 'shown' : 'hidden'}`}
                className={`tb-btn ${showTOS ? 'tb-btn-success-active' : ''}`}
                aria-pressed={showTOS}
                aria-label="Toggle ThinkOrSwim column"
              >
                <img src="https://www.opcioguru.com/static/trading2/img/thinkorswim.png" alt="" className="tb-btn-img" style={{ opacity: showTOS ? 1 : 0.6, filter: showTOS ? 'none' : 'grayscale(40%)' }} />
              </button>
            </div>

            <div className="toolbar-divider" aria-hidden="true" />

            {/* Group 4: Tools */}
            <div className="toolbar-group" aria-label="Tools">
              <button
                onClick={() => {
                  setShowOnlyPinned(prev => {
                    const next = !prev;
                    try { localStorage.setItem('showOnlyPinned', String(next)); } catch (_) {}
                    return next;
                  });
                }}
                title={
                  pinCount === 0
                    ? 'No pinned combinations yet — click a star to pin one'
                    : showOnlyPinned
                      ? `Showing only pinned (${pinCount}) — click to show all`
                      : `Show only pinned (${pinCount})`
                }
                className={`tb-btn tb-btn-pinfilter ${showOnlyPinned ? 'is-active' : ''}`}
                aria-pressed={showOnlyPinned}
                aria-label="Toggle show-only-pinned filter"
                disabled={pinCount === 0 && !showOnlyPinned}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden
                  fill={showOnlyPinned ? 'currentColor' : 'none'}
                  stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
                  <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                </svg>
                {pinCount > 0 && <span className="tb-btn-pincount">{pinCount}</span>}
              </button>

              <button
                onClick={() => setIsAlertsModalOpen(true)}
                title={
                  alertsConfig.enabled
                    ? `Alerts ON — ${(alertsConfig.rules || []).length} rule${(alertsConfig.rules || []).length === 1 ? '' : 's'} — click to configure`
                    : 'Configure alerts (currently OFF)'
                }
                className={`tb-btn tb-btn-bell ${alertsConfig.enabled ? 'is-active' : ''}`}
                aria-label="Configure alerts"
              >
                <span className="tb-btn-bell-icon">🔔</span>
              </button>

              <button
                onClick={() => setTheme(prev => prev === 'light' ? 'dark' : 'light')}
                className="theme-toggle"
                title={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
                aria-label="Toggle theme"
              >
                {theme === 'light' ? '🌙' : '☀️'}
              </button>
            </div>

            {/* Optional group: Ranking sliders */}
            {RANKING_FEATURE_ENABLED && (
              <>
                <div className="toolbar-divider" aria-hidden="true" />
                <div className="toolbar-group toolbar-group-sliders" aria-label="Ranking weights">
                  <label className="slider-label">R/R</label>
                  <input type="range" min="0" max="100" step="5" value={wRR} onChange={(e)=>setWRR(Number(e.target.value))} style={{ accentColor: '#4CAF50' }} />
                  <label className="slider-label">VoD</label>
                  <input type="range" min="0" max="100" step="5" value={wVoD} onChange={(e)=>setWVoD(Number(e.target.value))} style={{ accentColor: '#4CAF50' }} />
                  <label className="slider-label">Hedge</label>
                  <input type="range" min="0" max="100" step="5" value={wHedge} onChange={(e)=>setWHedge(Number(e.target.value))} style={{ accentColor: '#4CAF50' }} />
                  <button
                    onClick={() => { setWRR(DEFAULT_WEIGHTS.rr); setVoDAndHedge(); }}
                    title="Reset ranking weights to defaults"
                    className="tb-btn"
                    aria-label="Reset ranking weights"
                  >
                    ↺
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="ticker-center">
            <div className="ticker-chip" title={`SPX (S&P 500 Index) — ${formatIndex(spxPrice)}`}>
              <span className="ticker-chip-label">SPX</span>
              <span className="ticker-chip-value">{formatIndex(spxPrice)}</span>
            </div>
          </div>

          <div className="meta">
            {(() => {
              const ageMs = nowTick - new Date(lastUpdatedAt).getTime();
              const ageMin = Math.max(0, Math.floor(ageMs / 60000));
              const freshness = ageMin < 6 ? 'fresh' : ageMin < 15 ? 'stale' : 'old';
              const ageText = ageMin < 1 ? 'just now' : ageMin < 60 ? `${ageMin}m ago` : `${Math.floor(ageMin / 60)}h ${ageMin % 60}m ago`;
              const absoluteTime = new Date(lastUpdatedAt).toLocaleString('hu-HU', {
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', hour12: false,
              });
              return (
                <>
                  <div className="results-badge" title="Number of fusion combinations found">
                    <span className="results-badge-count">{fusionData.length}</span>
                    <span className="results-badge-label">result{fusionData.length === 1 ? '' : 's'}</span>
                  </div>
                  <div className={`status-updated status-updated-${freshness}`} title={`Last updated at ${absoluteTime}`}>
                    <span className={`status-dot status-dot-${freshness}`} aria-hidden="true" />
                    <span className="status-updated-text">Updated {ageText}</span>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
      {/* Placeholder for future controls like Scan button, Auto-refresh */}
      {/* <div className="controls">
        <button onClick={loadData} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh Data'}
        </button>
      </div> */}

      {error && <div className="error">Error: {error}</div>}
      
      <div className="main-content" ref={mainContentRef} style={{ display: viewMode === 'grid' ? 'block' : 'flex', position: 'relative' }}>
        {isSwitchingView && (
          <div style={{ 
            position: 'absolute', inset: 0, zIndex: 100, 
            background: 'var(--bg)', display: 'flex', 
            alignItems: 'center', justifyContent: 'center',
            opacity: 0.8 
          }}>
            <div className="graph-spinner" style={{ position: 'relative' }} />
          </div>
        )}
        {viewMode === 'split' && (
          <div 
            className="fusion-table-section"
            style={{
              width: `calc(${tableWidthPercent}% - ${dividerWidth / 2}px)`,
              display: 'block', 
              height: '100%',
              marginBottom: '0',
              transition: isDragging ? 'none' : 'width 0.1s ease-out',
              maxHeight: 'none', 
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            {loading && (
              <div className="skeleton skeleton-table" aria-hidden>
                <div className="skeleton-table-header">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div key={`h-${i}`} className="skeleton-chip" />
                  ))}
                </div>
                {Array.from({ length: 20 }).map((_, r) => (
                  <div key={`r-${r}`} className="skeleton-table-row">
                    {Array.from({ length: 12 }).map((_, c) => (
                      <div key={`r-${r}-c-${c}`} className="skeleton-chip" />
                    ))}
                  </div>
                ))}
              </div>
            )}
            {!loading && !error && fusionData.length === 0 && <div className="no-data">No fusion combinations found.</div>}
            {!loading && !error && fusionData.length > 0 && (
              <FusionTable 
                key={`${tableMetricMode}-${viewMode}`}
                data={nonPinnedRows}
                pinnedRows={pinnedRows}
                isPinned={isPinned}
                togglePin={togglePin}
                clearAllPins={clearAllPins}
                pinCount={pinCount}
                fusionType={fusionType}
                wRR={wRR} wVoD={wVoD} wHedge={wHedge}
                selectedCombinationId={selectedCombination?.id}
                setSelectedCombination={setSelectedCombination}
                sorting={sorting}
                onSortingChange={setSorting}
                columnFilters={columnFilters}
                onColumnFiltersChange={setColumnFilters}
                showOS={showOS}
                showTOS={showTOS}
                hideBody={false}
                tableMetricMode={tableMetricMode}
                setTableMetricMode={(next) => {
                  setTableMetricMode(next);
                  try { sessionStorage.setItem('tableMetricMode', next); } catch (_) {}
                }}
                viewMode={viewMode}
                onAnalysisDataUpdate={setAnalysisData}
                alertHighlightedRowIds={alertHighlightedRowIds}
              />
            )}
          </div>
        )}

        {viewMode === 'split' && (
          <>
            <div 
              className="divider"
              onMouseDown={handleMouseDown}
            />

            <div 
              className="graph-container"
              style={{
                width: `calc(${graphWidthPercent}% - ${dividerWidth / 2}px)`,
                transition: isDragging ? 'none' : 'width 0.1s ease-out'
              }}
            >
              <div className="risk-graph-wrapper">
                {loading ? (
                  <div className="graph-loader" aria-hidden>
                    <div className="graph-grid" />
                    <div className="graph-spinner" />
                  </div>
                ) : selectedCombination ? (
                  <RiskGraph selectedCombination={selectedCombination} />
                ) : (
                  <div className="no-graph">
                    {fusionData.length > 0 ? 'Select a combination from the table to view its risk graph.' : 'No data to display graph.'}
                  </div>
                )}
              </div>
              <div className="tradingview-widget-wrapper">
                <TradingViewWidget symbol="SP500" theme={theme} />
              </div>
            </div>
          </>
        )}

        {viewMode === 'grid' && !loading && (
          <div ref={gridScrollRef} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto', paddingRight: '5px' }}>
            {pinnedRows.length > 0 && (
              <div className="grid-pinned-header">
                <span className="grid-pinned-title">
                  <span className="star-icon" aria-hidden>★</span>
                  Pinned ({pinnedRows.length})
                </span>
                <button
                  type="button"
                  className="pin-clear-all"
                  onClick={clearAllPins}
                  title="Unpin all"
                >
                  Clear all
                </button>
              </div>
            )}
            {pinnedRows.length > 0 && (
              <div
                className="grid-view-container grid-view-pinned"
                style={{ gridTemplateColumns: `repeat(${gridColumns}, 1fr)` }}
              >
                {pinnedRows.map((item) => (
                  <CombinationCard
                    key={item.id}
                    item={item}
                    isSelected={selectedCombination?.id === item.id}
                    onSelect={() => setSelectedCombination(item)}
                    isAlertHighlighted={alertHighlightedRowIds.has(item.id)}
                    isPinned={true}
                    onTogglePin={() => togglePin(item)}
                    isStale={!!item._stale}
                  />
                ))}
              </div>
            )}
            <div
              className="grid-view-container"
              style={{ gridTemplateColumns: `repeat(${gridColumns}, 1fr)` }}
            >
              {paginatedRows.map((item) => (
                <CombinationCard
                  key={item.id}
                  item={item}
                  isSelected={selectedCombination?.id === item.id}
                  onSelect={() => setSelectedCombination(item)}
                  isAlertHighlighted={alertHighlightedRowIds.has(item.id)}
                  isPinned={isPinned(item)}
                  onTogglePin={() => togglePin(item)}
                />
              ))}
            </div>
            {totalGridPages > 0 && (
              <div className="grid-pagination" style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '16px',
                padding: '20px 0',
                borderTop: '1px solid var(--border)'
              }}>
                <button
                  onClick={() => handlePageChange(Math.max(1, gridPage - 1))}
                  disabled={gridPage === 1}
                  className="grid-pagination-btn"
                >
                  &laquo; Previous
                </button>
                <span className="grid-pagination-info">
                  Page {gridPage} of {totalGridPages}
                </span>
                <button
                  onClick={() => handlePageChange(Math.min(totalGridPages, gridPage + 1))}
                  disabled={gridPage === totalGridPages}
                  className="grid-pagination-btn"
                >
                  Next &raquo;
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <AlertsManager 
        isOpen={isAlertsModalOpen} 
        onClose={() => setIsAlertsModalOpen(false)} 
        config={alertsConfig} 
        setConfig={setAlertsConfig} 
      />
    </div>
  );
}

export default App;
